/**
 * Wave 50 · Thread view modal.
 *
 * Click a message in the inbox → this opens with the full thread
 * (root + replies). Reply box at the bottom: enter sends, parent_message_id
 * is the root, recipients are seeded with everyone in the thread minus
 * the current user.
 *
 * Design choices:
 *   - Same layout vocabulary as Compose so the two feel like siblings.
 *   - Avatars for author identity; system/agent rows get a special tag.
 *   - Auto-scroll to the bottom on open + on reply success.
 *   - No "edit" or "delete" actions inline — those happen from the
 *     inbox row's hover slot. This is read-mostly.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
// Wave chat-identity-fix · 2026-05-26 — JP reportou bug onde msg
// enviada por ele aparecia como se fosse do peer ("vegas"). Causa
// raiz: comparávamos `msg.authorUserId === me.id` mas `me.id`
// persistido no localStorage do mobile estava stale. JWT.sub é a
// única fonte que casa com o `author_user_id` gravado pelo backend.
import { useAuthoritativeUserId } from '@/lib/auth-identity';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/i18n';
import { MessageBodyRender } from '@/lib/message-body';
import { ReactionBar, type ReactionAggregate } from './ReactionBar';
import { FeatureBoundary } from '@/components/ErrorBoundary';
// PM-friction audit · 2026-05-20 — Skeleton no loading do thread.
import { Skeleton } from '@/components/ui/Skeleton';

type ThreadResp = Awaited<ReturnType<typeof api.getMessageThread>>;
type ThreadMsg = ThreadResp['messages'][number] & {
  attachments?: Array<{ id: string; filename: string; contentType: string; sizeBytes: number }>;
};

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Wave 333 · public Thread is the FeatureBoundary-wrapped form.
 * Internal logic lives in ThreadInner. Reason: Wave 316b had a render
 * crash in ReactionBar that took down /inbox to a blank screen; the
 * boundary keeps the inbox list rendered + retry button visible while
 * a thread payload glitch is investigated.
 */
export function Thread(props: ThreadProps) {
  return (
    <FeatureBoundary name="Thread">
      <ThreadInner {...props} />
    </FeatureBoundary>
  );
}

interface ThreadProps {
  open: boolean;
  messageId: string | null;
  onClose: () => void;
  onChanged: () => void;
}

function ThreadInner({
  open,
  messageId,
  onClose,
  onChanged,
}: ThreadProps) {
  const me = useAuth((s) => s.user);
  const { locale } = useT();
  const isEn = locale === 'en';
  // Wave chat-identity-fix · id autoritativo (JWT.sub). Usar `me.id`
  // direto colocava bubble do lado errado quando o localStorage do
  // mobile estava com user.id antigo.
  const meId = useAuthoritativeUserId();
  const [data, setData] = useState<ThreadResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Wave 316 — reactions aggregate for every message in the thread, fetched
  // in one round trip after the thread loads. Local state so optimistic
  // toggles in ReactionBar don't have to wait on a refetch.
  const [reactions, setReactions] = useState<ReactionAggregate[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !messageId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setDraft('');
    void (async () => {
      try {
        const t = await api.getMessageThread(messageId);
        if (alive) {
          setData(t);
          // Auto-scroll to last message after paint.
          queueMicrotask(() => {
            if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
          });
          // Wave 316 — fetch reactions for every message in the thread in
          // one round trip. Failure here is non-fatal: reactions are
          // ornament, not critical.
          //
          // Wave 316b · BUG FIX: api.listReactions returns the array
          // directly (request<T> auto-unwraps payload.data). Era `r.data`,
          // virou `r`. Mantive `?? []` como rede defensiva — nunca passamos
          // undefined pra setReactions.
          try {
            const r = await api.listReactions(t.messages.map((m) => m.id));
            if (alive) setReactions(Array.isArray(r) ? r : []);
          } catch {
            if (alive) setReactions([]);
          }
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : (isEn ? 'Failed to load' : 'Falha ao carregar'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, messageId]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Recipients for the reply: every distinct user in the thread except
  // me. Resolved client-side so we don't need a separate endpoint.
  // Wave chat-identity-fix · usa meId (JWT.sub) pra que "exceto eu"
  // case com o autor que o backend realmente gravou nas msgs.
  const replyRecipients = (() => {
    if (!data || !meId) return [] as string[];
    const ids = new Set<string>();
    for (const m of data.messages) {
      if (m.authorUserId && m.authorUserId !== meId) ids.add(m.authorUserId);
    }
    return Array.from(ids);
  })();

  const sendReply = async () => {
    if (!data || !draft.trim() || sending) return;
    setSending(true);
    try {
      await api.sendMessage({
        recipientUserIds: replyRecipients,
        body: draft.trim(),
        parentMessageId: data.rootId,
      });
      setDraft('');
      // Refetch the thread + tell parent inbox to refresh.
      const t = await api.getMessageThread(data.rootId);
      setData(t);
      onChanged();
      queueMicrotask(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : (isEn ? 'Failed to send' : 'Falha ao enviar'));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && messageId && (
        <>
          <motion.div
            className="fixed inset-0 z-[90] bg-ink/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal
            className={cn(
              'fixed z-[100] left-1/2 top-4 flex flex-col w-[min(640px,calc(100vw-2rem))]',
              '-translate-x-1/2 max-h-[calc(100dvh-2rem)] md:top-[8vh] md:max-h-[84vh] overflow-hidden rounded-2xl border border-line bg-surface shadow-elev-3',
            )}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <header className="flex items-center justify-between border-b border-line px-4 py-3">
              <p className="truncate text-sm font-semibold text-ink">
                {data?.messages[0]?.subject ?? (isEn ? 'Conversation' : 'Conversa')}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="pressable rounded-lg px-2 py-1 text-sm text-ink-muted hover:text-ink"
                aria-label={isEn ? 'Close' : 'Fechar'}
              >
                ✕
              </button>
            </header>

            <div ref={bodyRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm">
              {loading && (
                <div className="space-y-3" data-testid="thread-loading">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-12 w-3/4 opacity-70" />
                  <Skeleton className="h-12 w-2/3 opacity-50" />
                </div>
              )}
              {error && (
                <p className="rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-xs text-err">
                  {error}
                </p>
              )}
              {data?.messages.map((m) => (
                <ThreadMessageRow
                  key={m.id}
                  msg={m}
                  // Wave chat-identity-fix · meId autoritativo (JWT.sub).
                  // Bubble "minha" vs "peer" só fica certa com isto.
                  myId={meId ?? ''}
                  isEn={isEn}
                  reactions={reactions}
                  onReactionsChange={setReactions}
                />
              ))}
            </div>

            {/* Reply composer — only on user-kind threads. System and
                agent_alert rows are one-way notifications. */}
            {data && data.messages[0]?.kind === 'user' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void sendReply();
                }}
                className="flex items-end gap-2 border-t border-line px-3 py-3"
              >
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void sendReply();
                    }
                  }}
                  placeholder={
                    replyRecipients.length === 0
                      ? (isEn ? 'No recipients — this is just you. Use the New button to start a new conversation.' : 'Sem destinatários — esse é só você. Use o botão Nova pra começar uma nova conversa.')
                      : (isEn ? 'Reply…' : 'Responder…')
                  }
                  rows={1}
                  disabled={replyRecipients.length === 0}
                  className="flex-1 resize-none rounded-xl border border-line bg-surface-2 px-3 py-2 text-[14px] text-ink placeholder:text-ink-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25 disabled:opacity-50"
                  style={{ minHeight: 40, maxHeight: 140 }}
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || sending || replyRecipients.length === 0}
                  className="pressable inline-flex h-10 items-center rounded-xl bg-ink px-4 text-sm font-medium text-surface hover:bg-ink/95 disabled:opacity-50"
                >
                  {sending ? (isEn ? 'Sending…' : 'Enviando…') : (isEn ? 'Reply' : 'Responder')}
                </button>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ThreadMessageRow({
  msg,
  myId,
  isEn,
  reactions,
  onReactionsChange,
}: {
  msg: ThreadMsg;
  myId: string;
  isEn: boolean;
  reactions: ReactionAggregate[];
  onReactionsChange: (next: ReactionAggregate[]) => void;
}) {
  const mine = msg.authorUserId === myId;
  // Wave 319 · BUG FIX (timezone): era `new Date(msg.createdAt)` direto +
  // `toLocaleString` sem timeZone option. Como o backend retorna SQLite
  // datetime sem 'Z', JS parseava como local (BRT) e mostrava UTC bruto.
  // Agora delegamos pro formatDateTime que usa parseServerDate + timeZone
  // 'America/Sao_Paulo'. 11pm BRT mostra "23:00", não "02:00".
  const when = formatDateTime(msg.createdAt);
  // Wave 305 — surface the original email sender + a one-click block
  // button on inbound-email-sourced messages. Source starts with
  // 'inbound_email'; metadata.inbound_email_from has the raw address.
  const isInboundEmail = typeof msg.source === 'string' && msg.source.startsWith('inbound_email');
  const meta = (msg.metadata ?? {}) as Record<string, unknown>;
  const fromRaw = typeof meta.inbound_email_from === 'string' ? meta.inbound_email_from : null;
  const fromEmail = fromRaw
    ? fromRaw.toLowerCase().replace(/.*<([^>]+)>.*/, '$1').trim()
    : null;
  // Wave 306 — auth-results from CF Email Routing. `from_verified` is
  // false when SPF/DKIM/DMARC failed; null when CF didn't include the
  // header. We only flag explicit failures — null stays neutral.
  const fromVerified = meta.from_verified;
  const authFailed = fromVerified === false;
  const who =
    msg.kind === 'agent_alert'
      ? 'Housy'
      : msg.kind === 'system'
        ? (isEn ? 'System' : 'Sistema')
        : (msg.author?.fullName ?? (isEn ? 'Someone' : 'Alguém'));
  return (
    <div className={cn('flex gap-3', mine && 'flex-row-reverse')}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-400/20 text-[11px] font-semibold uppercase text-brand-700 dark:text-brand-400">
        {initials(who)}
      </div>
      <div className={cn('min-w-0 flex-1', mine && 'text-right')}>
        <div className="flex items-baseline gap-2 text-[11px] text-ink-subtle">
          <span className={cn('font-medium text-ink', mine && 'order-2')}>{who}</span>
          <span className={cn(mine && 'order-1')}>{when}</span>
        </div>
        <div
          className={cn(
            'mt-1 inline-block max-w-full rounded-xl px-3 py-2 text-left',
            mine ? 'bg-ink text-surface' : 'bg-surface-2 text-ink',
          )}
        >
          <p className="whitespace-pre-wrap leading-relaxed">
            <MessageBodyRender body={msg.body} />
          </p>
          {isInboundEmail && fromEmail && (
            <EmailSenderChip
              fromEmail={fromEmail}
              mine={mine}
              authFailed={authFailed}
              isEn={isEn}
            />
          )}
          {msg.attachments && msg.attachments.length > 0 && (
            <div className={cn('mt-2 flex flex-wrap gap-1.5', mine && 'justify-end')}>
              {msg.attachments.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  // The download endpoint is auth-gated, so we can't
                  // navigate to it as a plain href (browser strips our
                  // Authorization header). Instead, fetch with bearer
                  // and trigger a blob download.
                  onClick={(e) => {
                    e.stopPropagation();
                    void api.downloadMessageAttachment(a.id, a.filename);
                  }}
                  className={cn(
                    'pressable inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px]',
                    mine
                      ? 'border-surface/30 bg-surface/10 text-surface hover:bg-surface/20'
                      : 'border-line bg-surface text-ink hover:bg-surface-3',
                  )}
                  title={a.filename}
                >
                  <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M10.5 3.5l-5.3 5.3a2 2 0 1 0 2.83 2.83L13 6.66a3 3 0 0 0-4.24-4.24L3.5 7.66a4 4 0 0 0 5.66 5.66L13 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="max-w-[180px] truncate">{a.filename}</span>
                  <span className="opacity-70">{formatBytes(a.sizeBytes)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Wave 316 — reaction bar lives outside the bubble so it sits in
            the row gutter (works for both light/dark fills) and never
            tints in the dark "mine" bubble. */}
        <ReactionBar
          messageId={msg.id}
          reactions={reactions}
          onChange={onReactionsChange}
          align={mine ? 'right' : 'left'}
        />
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Wave 305 — small chip on inbound-email-sourced messages with one-click
// block actions. Two flavors: block this exact address, or block the
// whole domain. Both write to mail_blocklist via api.blockEmailSender;
// next inbound from that sender is dropped at the email-worker before
// it touches D1 or R2.
function EmailSenderChip({
  fromEmail,
  mine,
  authFailed,
  isEn,
}: {
  fromEmail: string;
  mine: boolean;
  authFailed?: boolean;
  isEn: boolean;
}) {
  const [status, setStatus] = useState<'idle' | 'blocking' | 'blocked' | 'error'>('idle');
  const domain = fromEmail.split('@')[1] ?? '';
  const onBlock = async (kind: 'address' | 'domain') => {
    setStatus('blocking');
    try {
      await api.blockEmailSender(kind === 'domain' ? domain : fromEmail);
      setStatus('blocked');
    } catch {
      setStatus('error');
    }
  };
  return (
    <div className={cn('mt-2 flex flex-wrap items-center gap-2 text-[11px]', mine && 'justify-end')}>
      <span className="rounded-full bg-surface px-2 py-0.5 text-ink-subtle">
        📧 {fromEmail}
      </span>
      {authFailed && (
        <span
          className="rounded-full bg-warn-bg px-2 py-0.5 text-warn"
          title={isEn ? 'SPF/DKIM/DMARC failed — sender may be spoofed. Verify before trusting.' : 'SPF/DKIM/DMARC falhou — remetente pode estar falsificado. Confirme antes de confiar.'}
        >
          {isEn ? '⚠ unverified' : '⚠ não verificado'}
        </span>
      )}
      {status === 'idle' && (
        <>
          <button
            type="button"
            onClick={() => void onBlock('address')}
            className="pressable rounded-full border border-line px-2 py-0.5 text-ink-muted hover:bg-surface-2 hover:text-ink"
            title={isEn ? 'Block this sender' : 'Bloquear este remetente'}
          >
            🚫 {isEn ? 'block' : 'bloquear'}
          </button>
          {domain && (
            <button
              type="button"
              onClick={() => void onBlock('domain')}
              className="pressable rounded-full border border-line px-2 py-0.5 text-ink-muted hover:bg-surface-2 hover:text-ink"
              title={isEn ? `Block all emails from @${domain}` : `Bloquear todos os emails de @${domain}`}
            >
              🚫 @{domain}
            </button>
          )}
        </>
      )}
      {status === 'blocking' && <span className="text-ink-subtle">{isEn ? 'blocking…' : 'bloqueando…'}</span>}
      {status === 'blocked' && <span className="text-ok">{isEn ? 'blocked ✓' : 'bloqueado ✓'}</span>}
      {status === 'error' && <span className="text-error">{isEn ? 'error blocking' : 'erro ao bloquear'}</span>}
    </div>
  );
}
